import Cart from "cart-store";

const COPY = {
    empty: "כתבו קוד קופון",
    checking: "בודקים…",
    applied: "הקוד נוסף",
    invalid: "הקוד לא תקף לעגלה הזו",
    duplicate: "הקוד כבר בעגלה",
    error: "משהו השתבש, נסו שוב",
    removing: "מסירים…",
    chipInactive: "לא חל על הפריטים"
};

let busy = false;
let suppressMessageClear = false;
let listening = false;
let codesPromise = null;

function sameCode(a, b) {
    return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

function cleanCode(value) {
    return String(value || "").replace(/[\u0000-\u001F\u007F]/g, "").trim();
}

function codeEntries(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return null;
    return cart.discount_codes.filter((entry => entry && entry.code)).map((entry => ({
        code: String(entry.code),
        applicable: entry.applicable === true
    })));
}

function codesSignature(entries) {
    return (entries || []).map((entry => `${entry.code}:${entry.applicable ? "1" : "0"}`)).join("|");
}

function uniqueCodes(codes) {
    return codes.reduce(((list, code) => {
        if (code && !list.some((item => sameCode(item, code)))) list.push(code);
        return list;
    }), []);
}

// The discount field replaces every code, so the first request includes the
// codes already on the cart plus the one just typed. This store answers
// applicable:false for any string it does not apply, including codes that do
// not exist, and it keeps those strings on the cart. A code the customer just
// typed is kept only when it actually applies. `restore` is only a signal that
// the attempt response is not the list we want. The follow-up must read
// /cart.js again and must not post this remembered list: another app may have
// added a code, attribute, or note since the attempt started.
function planDiscountChange(previous, returned, attemptedCode) {
    const prev = previous || [];
    const next = returned || [];
    const hit = attemptedCode ? next.find((entry => sameCode(entry.code, attemptedCode))) : null;
    const previousCodes = uniqueCodes(prev.map((entry => entry.code)));
    const missingPrevious = prev.filter((entry => !next.some((item => sameCode(item.code, entry.code)))));
    if (hit && hit.applicable) {
        return {
            outcome: "applied",
            restore: missingPrevious.length ? uniqueCodes([ ...previousCodes, hit.code ]) : null
        };
    }
    const keptTypedCode = Boolean(hit);
    return {
        outcome: "rejected",
        restore: keptTypedCode || missingPrevious.length ? previousCodes : null
    };
}

function cartUrl(path) {
    const root = window.Shopify?.routes?.root ?? "/";
    return `${root.replace(/\/?$/, "/")}${path}`;
}

function motionAllowed() {
    const sample = document.querySelector("cart-discount-code");
    if (!sample || sample.dataset.animations === "false") return false;
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function syncMotionClass() {
    document.documentElement.classList.toggle("cart-discount-motion", motionAllowed());
}

function eachBox(callback) {
    document.querySelectorAll("cart-discount-code").forEach(callback);
}

function setBusy(on) {
    busy = on;
    eachBox((box => box.setBusy(on)));
}

function broadcastStatus(type, text, shake) {
    eachBox((box => {
        box.setStatus(type, text);
        if (shake) box.shake();
    }));
}

function clearInputs() {
    eachBox((box => {
        if (!box.input) return;
        box.input.value = "";
    }));
}

function pulseTotals() {
    if (!document.documentElement.classList.contains("cart-discount-motion")) return;
    const targets = new Set;
    document.querySelectorAll("[data-cart-subtotal]").forEach((node => {
        targets.add(node.closest(".cart__total") || node);
    }));
    targets.forEach((node => {
        node.classList.remove("is-discount-pulse");
        void node.offsetWidth;
        node.classList.add("is-discount-pulse");
        node.addEventListener("animationend", (() => node.classList.remove("is-discount-pulse")), {
            once: true
        });
    }));
}

async function readJson(response) {
    const data = await response.json();
    if (!response.ok || data && data.status && data.status >= 400) {
        const error = new Error(data && (data.description || data.message) || "cart-request-failed");
        error.payload = data;
        throw error;
    }
    return data;
}

function fetchCart() {
    return fetch(cartUrl("cart.js"), {
        headers: {
            Accept: "application/json"
        },
        credentials: "same-origin",
        cache: "no-store"
    }).then(readJson);
}

function updateDiscount(codes, signal) {
    return fetch(cartUrl("cart/update.js"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({
            discount: codes.filter(Boolean).join(",")
        }),
        signal: signal
    }).then(readJson);
}

async function readFreshCart(signal) {
    const cart = await fetchCart();
    if (signal && signal.aborted) throw new DOMException("The operation was aborted.", "AbortError");
    return cart;
}

// Drop one code the customer just typed. The other codes are whatever /cart.js
// says at this moment, so a members-club code added in the meantime stays.
// The request sends only the discount list: it does not send a note or attributes.
async function removeCodeFromFreshCart(code, signal) {
    const fresh = await readFreshCart(signal);
    const entries = codeEntries(fresh);
    if (!entries) throw new Error("missing-discount-codes");
    if (!entries.some((entry => sameCode(entry.code, code)))) return fresh;
    return updateDiscount(codesExcept(entries, code), signal);
}

function publishCart(cart) {
    const current = Cart.getState().cart || {};
    const next = {
        ...cart
    };
    if (next.note == null && current.note != null) next.note = current.note;
    if (next.attributes == null && current.attributes != null) next.attributes = current.attributes;
    suppressMessageClear = true;
    try {
        return Cart.replaceCart(next);
    } finally {
        suppressMessageClear = false;
    }
}

// Another app (SimplyClub) can repeat the cart update that still contained the
// code we just rejected, and that repeat sometimes lands after our restore.
// Watch the cart for a short time, and if that code comes back, take it off
// again without drawing a chip for it. Two removals is the limit.
const REJECT_REREAD_MS = 1100;
const REJECT_WATCH_MS = 2000;
const MAX_REJECT_STRIPS = 2;

let rejectWatch = null;
let rejectGeneration = 0;

function disarmRejectWatch() {
    const watch = rejectWatch;
    rejectWatch = null;
    rejectGeneration += 1;
    if (!watch) return;
    watch.generation = -1;
    watch.timers.forEach((id => window.clearTimeout(id)));
    window.clearTimeout(watch.eventTimer);
    watch.controller.abort();
}

function armRejectWatch(code) {
    disarmRejectWatch();
    const generation = ++rejectGeneration;
    const watch = {
        code: code,
        generation: generation,
        until: Date.now() + REJECT_WATCH_MS,
        strips: 0,
        checking: false,
        queued: false,
        timers: [],
        eventTimer: 0,
        controller: new AbortController
    };
    rejectWatch = watch;
    queueRejectRead(watch, REJECT_REREAD_MS);
    queueRejectRead(watch, REJECT_WATCH_MS);
}

function queueRejectRead(watch, delay) {
    const id = window.setTimeout((() => {
        readRejectedCart(watch);
    }), delay);
    watch.timers.push(id);
}

function noteRejectCartActivity() {
    const watch = rejectWatch;
    if (!watch || suppressMessageClear || watch.generation !== rejectGeneration) return;
    if (Date.now() > watch.until) return;
    window.clearTimeout(watch.eventTimer);
    watch.eventTimer = window.setTimeout((() => {
        readRejectedCart(watch);
    }), 120);
}

function codesExcept(entries, code) {
    return uniqueCodes((entries || []).filter((entry => !sameCode(entry.code, code))).map((entry => entry.code)));
}

async function readRejectedCart(watch) {
    if (!watch || watch.generation !== rejectGeneration || rejectWatch !== watch) return;
    if (watch.checking) {
        watch.queued = true;
        return;
    }
    watch.checking = true;
    try {
        const fresh = await fetchCart();
        if (watch.generation !== rejectGeneration) return;
        const entries = codeEntries(fresh);
        if (!entries) return;
        const back = entries.some((entry => sameCode(entry.code, watch.code)));
        if (!back) {
            const shown = codeEntries(Cart.getState().cart) || [];
            if (shown.some((entry => sameCode(entry.code, watch.code)))) publishCart(fresh);
            if (Date.now() + 40 >= watch.until) disarmRejectWatch();
            return;
        }
        if (watch.strips >= MAX_REJECT_STRIPS) {
            disarmRejectWatch();
            publishCart(fresh);
            return;
        }
        watch.strips += 1;
        const cleaned = await removeCodeFromFreshCart(watch.code, watch.controller.signal);
        if (watch.generation !== rejectGeneration) return;
        const cleanedEntries = codeEntries(cleaned);
        if (cleanedEntries && !cleanedEntries.some((entry => sameCode(entry.code, watch.code)))) publishCart(cleaned);
        watch.until = Date.now() + REJECT_WATCH_MS;
        queueRejectRead(watch, REJECT_REREAD_MS);
        queueRejectRead(watch, REJECT_WATCH_MS);
    } catch (error) {
        if (error && error.name === "AbortError") return;
    } finally {
        watch.checking = false;
        if (watch.queued && watch.generation === rejectGeneration && rejectWatch === watch) {
            watch.queued = false;
            readRejectedCart(watch);
        }
    }
}

function ensureDiscountCodes() {
    const cart = Cart.getState().cart;
    if (Array.isArray(cart?.discount_codes)) return Promise.resolve(cart);
    if (!codesPromise) {
        codesPromise = fetchCart().then((fresh => publishCart(fresh))).catch((() => cart)).finally((() => {
            codesPromise = null;
        }));
    }
    return codesPromise;
}

function listenForCartChanges() {
    if (listening) return;
    listening = true;
    const sync = cart => {
        if (!cart) return;
        noteRejectCartActivity();
        eachBox((box => box.syncFromCart(cart)));
    };
    Cart.subscribe((state => sync(state.cart)));
    document.addEventListener("on:cart:loaded", (event => {
        sync(event.detail && event.detail.cart);
    }));
}

class CartDiscountCode extends HTMLElement {
    connectedCallback() {
        syncMotionClass();
        listenForCartChanges();
        if (!this.bound) {
            this.bound = true;
            this.form = this.querySelector("[data-discount-form]");
            this.input = this.querySelector("[data-discount-input]");
            this.submitButton = this.querySelector("[data-discount-submit]");
            this.spinner = this.querySelector("[data-discount-spinner]");
            this.message = this.querySelector("[data-discount-message]");
            this.list = this.querySelector("[data-discount-codes]");
            this.codesSignature = "";
            this.form.addEventListener("submit", (event => this.onSubmit(event)));
            this.list.addEventListener("click", (event => this.onRemove(event)));
            this.input.addEventListener("input", (() => {
                this.input.classList.remove("is-invalid");
                this.input.removeAttribute("aria-invalid");
            }));
        }
        this.setBusy(busy);
        this.syncFromCart(Cart.getState().cart);
        ensureDiscountCodes();
    }
    setBusy(on) {
        this.classList.toggle("is-busy", on);
        if (on) this.setAttribute("aria-busy", "true");
        else this.removeAttribute("aria-busy");
        if (this.submitButton) this.submitButton.disabled = on;
        if (this.input) this.input.readOnly = on;
        if (this.spinner) this.spinner.hidden = !on;
        this.querySelectorAll("[data-discount-remove]").forEach((button => {
            button.disabled = on;
        }));
    }
    motionEnabled() {
        return this.dataset.animations !== "false" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
    setStatus(type, text) {
        if (!this.message) return;
        this.message.className = "cart-discount-code__message";
        if (type !== "error" && this.input) {
            this.input.classList.remove("is-invalid");
            this.input.removeAttribute("aria-invalid");
        }
        if (text && type) this.message.classList.add(`is-${type}`);
        if (text && this.motionEnabled()) {
            this.message.classList.remove("is-visible");
            void this.message.offsetWidth;
            this.message.classList.add("is-visible");
        }
        const announce = () => {
            this.message.textContent = text || "";
        };
        if (text && this.message.textContent === text) {
            this.message.textContent = "";
            window.requestAnimationFrame(announce);
            return;
        }
        announce();
    }
    shake() {
        if (!this.input) return;
        this.input.classList.add("is-invalid");
        this.input.setAttribute("aria-invalid", "true");
        if (!this.motionEnabled()) return;
        this.input.classList.remove("is-shaking");
        void this.input.offsetWidth;
        this.input.classList.add("is-shaking");
        this.input.addEventListener("animationend", (() => this.input.classList.remove("is-shaking")), {
            once: true
        });
    }
    syncFromCart(cart) {
        let entries = codeEntries(cart);
        if (!entries || !this.list) return;
        if (rejectWatch) entries = entries.filter((entry => !sameCode(entry.code, rejectWatch.code)));
        const signature = codesSignature(entries);
        if (signature === this.codesSignature) return;
        const hadCodes = this.codesSignature !== "";
        this.codesSignature = signature;
        this.renderCodes(entries);
        if (hadCodes && !suppressMessageClear) this.setStatus("", "");
    }
    renderCodes(entries) {
        const incoming = new Map(entries.map((entry => [ entry.code.toLowerCase(), entry ])));
        Array.from(this.list.children).forEach((chip => {
            const key = (chip.dataset.code || "").toLowerCase();
            if (chip.dataset.leaving === "true") {
                chip.remove();
                return;
            }
            const entry = incoming.get(key);
            if (!entry) {
                this.removeChip(chip);
                return;
            }
            incoming.delete(key);
            this.updateChip(chip, entry);
        }));
        incoming.forEach((entry => {
            const chip = this.createChip(entry);
            if (this.motionEnabled()) chip.classList.add("is-entering");
            this.list.appendChild(chip);
        }));
    }
    createChip(entry) {
        const chip = document.createElement("li");
        chip.className = "cart-discount-code__chip";
        chip.dataset.code = entry.code;
        const text = document.createElement("span");
        text.className = "cart-discount-code__chip-text";
        const code = document.createElement("span");
        code.className = "cart-discount-code__chip-code";
        code.textContent = entry.code;
        text.appendChild(code);
        const state = document.createElement("span");
        state.className = "cart-discount-code__chip-state";
        text.appendChild(state);
        chip.appendChild(text);
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "cart-discount-code__remove";
        remove.dataset.discountRemove = entry.code;
        remove.textContent = "✕";
        chip.appendChild(remove);
        this.updateChip(chip, entry);
        return chip;
    }
    updateChip(chip, entry) {
        chip.classList.toggle("cart-discount-code__chip--inactive", !entry.applicable);
        const state = chip.querySelector(".cart-discount-code__chip-state");
        if (state) state.textContent = entry.applicable ? "" : COPY.chipInactive;
        const remove = chip.querySelector("[data-discount-remove]");
        if (remove) {
            remove.dataset.discountRemove = entry.code;
            remove.setAttribute("aria-label", `הסרת הקוד ${entry.code}`);
            remove.disabled = busy;
        }
    }
    removeChip(chip) {
        if (chip.dataset.leaving === "true") return;
        chip.dataset.leaving = "true";
        if (!this.motionEnabled()) {
            chip.remove();
            return;
        }
        chip.classList.add("is-leaving");
        const done = () => chip.remove();
        const timer = window.setTimeout(done, 200);
        chip.addEventListener("animationend", (() => {
            window.clearTimeout(timer);
            done();
        }), {
            once: true
        });
    }
    async onSubmit(event) {
        event.preventDefault();
        if (busy) return;
        const code = cleanCode(this.input.value);
        if (!code) {
            broadcastStatus("error", COPY.empty, true);
            return;
        }
        if (code.includes(",")) {
            broadcastStatus("error", COPY.invalid, true);
            return;
        }
        const seen = codeEntries(Cart.getState().cart) || [];
        if (seen.some((entry => sameCode(entry.code, code)))) {
            broadcastStatus("note", COPY.duplicate, false);
            return;
        }
        setBusy(true);
        disarmRejectWatch();
        broadcastStatus("note", COPY.checking, false);
        try {
            const baseline = await readFreshCart();
            const previous = codeEntries(baseline);
            if (!previous) throw new Error("missing-discount-codes");
            if (previous.some((entry => sameCode(entry.code, code)))) {
                publishCart(baseline);
                broadcastStatus("note", COPY.duplicate, false);
                return;
            }
            const beforeTotal = Number(baseline.total_price) || 0;
            let cart = await updateDiscount(uniqueCodes([ ...previous.map((entry => entry.code)), code ]));
            const returned = codeEntries(cart);
            if (!returned) throw new Error("missing-discount-codes");
            const plan = planDiscountChange(previous, returned, code);
            if (plan.outcome === "applied") {
                const fresh = await readFreshCart();
                const entries = codeEntries(fresh);
                if (!entries) throw new Error("missing-discount-codes");
                if (!entries.some((entry => sameCode(entry.code, code)))) {
                    cart = await updateDiscount(uniqueCodes([ ...entries.map((entry => entry.code)), code ]));
                } else cart = fresh;
                publishCart(cart);
                clearInputs();
                broadcastStatus("success", COPY.applied, false);
                if (Number(cart.total_price) < beforeTotal) pulseTotals();
            } else {
                cart = await removeCodeFromFreshCart(code);
                publishCart(cart);
                broadcastStatus("error", COPY.invalid, true);
                armRejectWatch(code);
            }
        } catch (error) {
            broadcastStatus("error", COPY.error, true);
            try {
                publishCart(await fetchCart());
            } catch (refreshError) {}
        } finally {
            setBusy(false);
        }
    }
    async onRemove(event) {
        const button = event.target.closest("[data-discount-remove]");
        if (!button || busy) return;
        const code = button.dataset.discountRemove;
        disarmRejectWatch();
        const beforeTotal = Number(Cart.getState().cart?.total_price) || 0;
        setBusy(true);
        broadcastStatus("note", COPY.removing, false);
        try {
            const cart = await removeCodeFromFreshCart(code);
            publishCart(cart);
            broadcastStatus("", "", false);
            if (Number(cart.total_price) < beforeTotal) pulseTotals();
        } catch (error) {
            broadcastStatus("error", COPY.error, false);
            try {
                publishCart(await fetchCart());
            } catch (refreshError) {}
        } finally {
            setBusy(false);
        }
    }
}

if (!customElements.get("cart-discount-code")) {
    customElements.define("cart-discount-code", CartDiscountCode);
}

export { planDiscountChange, codeEntries, sameCode, cleanCode, uniqueCodes };
